import React from 'react';
import { StaticPageLayout } from '../StaticPageLayout';
import styles from '../StaticPageLayout.module.scss';
import { LAST_UPDATED_DATE } from '../../../utils/constants';
import {
  CONTACT_EMAIL,
  CONTACT_MAILTO,
  COMPANY_LEGAL_NAME,
  COMPANY_CITY,
  COMPANY_COUNTRY,
} from '../../../utils/contactInfo';

export const TermsOfService: React.FC = () => {
  return (
    <StaticPageLayout title="Terms of Service">
      <div className={styles.contentWrapper}>
        <p>Last updated: {LAST_UPDATED_DATE}</p>

        <h2>1. Agreement</h2>
        <p>
          These Terms of Service ("Terms") form a legally binding agreement between you and{' '}
          {COMPANY_LEGAL_NAME} ("MyMinu", "we", "us"). By creating an account or using MyMinu, you
          accept these Terms. If you do not accept them, please do not use the Service.
        </p>

        <h2>2. The Service</h2>
        <p>
          MyMinu provides a visual routine-management tool for families and educators. We may add,
          change, or remove features at any time. We will give reasonable notice of changes that
          materially reduce the functionality you rely on.
        </p>

        <h2>3. Eligibility & Children</h2>
        <p>
          You must be at least 16 years old, or the age of digital consent in your country, to
          create an account. MyMinu is designed for parents and educators to manage routines on
          behalf of children. Children under 16 may use the app only under the supervision of
          a parent or guardian who has agreed to these Terms.
        </p>

        <h2>4. Your Account</h2>
        <p>
          You are responsible for keeping your password confidential and for any activity under
          your account. Notify us immediately at <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a> if
          you suspect unauthorised access. We are not liable for losses caused by your failure
          to keep your credentials secure.
        </p>

        <h2>5. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose</li>
          <li>Attempt to gain unauthorised access to our systems or other users' accounts</li>
          <li>Probe, scan, or test the vulnerability of our infrastructure</li>
          <li>Reverse-engineer, decompile, or attempt to extract source code, except where allowed by law</li>
          <li>Resell or sublicense the Service without our written permission</li>
          <li>Upload content that is harmful, abusive, or infringes third-party rights</li>
        </ul>

        <h2>6. Intellectual Property</h2>
        <p>
          MyMinu, the MyMinu logo, the visual routine clock, and all related designs and software
          are owned by {COMPANY_LEGAL_NAME} and protected by copyright, trademark, and other
          applicable laws. You retain ownership of any content you upload, but you grant us a
          worldwide, royalty-free licence to host, store, and display that content solely so we
          can provide the Service to you.
        </p>

        <h2>7. Pricing & Subscriptions</h2>
        <p>
          MyMinu may offer free and paid plans. Pricing for paid plans is shown at the point of
          sale. Subscriptions renew automatically until cancelled. You can cancel at any time
          from your account settings; cancellation takes effect at the end of the current billing
          period and is not refundable except where required by law.
        </p>

        <h2>8. Data & Privacy</h2>
        <p>
          Your use of the Service is also governed by our{' '}
          <a href="/legal/privacy">Privacy Policy</a>, which explains what data we collect and
          how we use it. You can export your data and request deletion as described there.
        </p>

        <h2>9. Termination</h2>
        <p>
          You may delete your account at any time. We may suspend or terminate your account if
          you breach these Terms or use the Service in a way that risks the safety or integrity
          of other users. Where reasonable, we will give you prior notice and an opportunity to
          remedy the breach.
        </p>

        <h2>10. Disclaimer of Warranties</h2>
        <p>
          To the fullest extent permitted by law, MyMinu is provided "as is" and "as available".
          We do not warrant that the Service will be uninterrupted, error-free, or fit for a
          particular purpose. Statutory consumer rights are not affected.
        </p>

        <h2>11. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by law, our total liability for any claim arising out
          of or relating to the Service is limited to the amount you paid us for the Service in
          the 12 months preceding the event giving rise to the claim. We are not liable for
          indirect, incidental, or consequential damages. Liability for death, personal injury
          caused by negligence, fraud, or other liability that cannot be excluded by law is not
          limited.
        </p>

        <h2>12. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. We will post the updated Terms here with a
          new "Last updated" date and, for material changes, notify you by email at least 14 days
          before they take effect. Continued use after that date constitutes acceptance.
        </p>

        <h2>13. Governing Law & Disputes</h2>
        <p>
          These Terms are governed by the laws of the Federal Republic of Germany, without regard
          to conflict-of-laws rules. The exclusive place of jurisdiction for any dispute arising
          out of or in connection with these Terms is {COMPANY_CITY}, {COMPANY_COUNTRY}, to the
          extent permitted by law. Mandatory consumer-protection provisions of your country of
          residence remain unaffected.
        </p>

        <h2>14. Contact</h2>
        <p>
          Questions about these Terms? Email{' '}
          <a href={CONTACT_MAILTO}>{CONTACT_EMAIL}</a>.
        </p>
      </div>
    </StaticPageLayout>
  );
};
